import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import CssBaseline from '@material-ui/core/CssBaseline';

import PdfPreviewDialog from '../dialogs/PdfPreviewDialog/PdfPreviewDialog';
import useRoutes from '../hooks/useRoutes';
import { ReduxState } from '../redux/types';
import AppBar from './components/AppBar/AppBar';
import Drawer from './components/Drawer/Drawer';
import PageRouter from './components/Router/Router';
import { Content, MainContainer } from './styled';

/*
 * In this component the skeleton of the application is created.
 * The top bar, the sidebar and the page router are loaded.
 */

const PageContainer = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const location = useLocation();

    const routes = useRoutes();

    const userInfo = useSelector((state: ReduxState) => state.user);

    const isLoggedIn = useMemo(() => {
        return userInfo.id > 0;
    }, [userInfo]);

    const showSidebar = useMemo(() => {
        // returns true or false if the sidebar should be shown or not, depending on the current page

        if (isLoggedIn && !userInfo.privacy && userInfo.type !== 'admin') {
            // if the user has not yet accepted the privacy policy and is not an admin, hide the sidebar
            return false;
        }

        const matchingRoute = routes.find(route => {
            return matchPath(location.pathname, {
                path: route.path,
                exact: route.exactPath
            }) !== null;
        });

        if (matchingRoute) {
            return matchingRoute.showSidebar === true;
        }

        return false;
    }, [isLoggedIn, location, routes, userInfo]);

    useEffect(() => {
        if (!showSidebar) {
            setIsDrawerOpen(false);
        }
    }, [showSidebar]);

    return (
        <MainContainer>
            <CssBaseline/>
            <AppBar showSidebar={showSidebar} openDrawer={() => setIsDrawerOpen(true)} />

            <Drawer show={showSidebar} isOpen={isDrawerOpen} setIsOpen={setIsDrawerOpen} />

            <Content showSidebar={showSidebar ? 1 : 0}>
                <PageRouter />
                <PdfPreviewDialog />
            </Content>
        </MainContainer>
    );
};

export default PageContainer;
